import { useState, useMemo, useEffect, useContext, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import SoftBox from "components/SoftBox";
import Tooltip from "@mui/material/Tooltip";
import DataTable from "cxfcomponents/Tables/DataTable";
import StatusBadge from "cxfcomponents/StatusBadge";
import NoDataIcon from "layouts/Flow Designer/components/NoDataIcon";
import UsersCell from "layouts/Flows/components/UsersCell";
import {
	closeFlowListDialog,
	fetchUserFlows,
	setSavedFlow
} from "redux/actions/flowDesigner/flowsActionCreator";
import { fetchUsers } from "redux/actions/flowDesigner/usersActionCreator";
import { getLocalDateTimeString } from "helpers/common";
import DialogWrapper from "../DialogWrapper";
import defaultFlowIcon from "assets/images/for-flow/flow-icon-list-view.svg";
import TextWithEllipsis from "layouts/pages/logs/components/TextWithEllipsis";
import { UserContext } from "context/AppProvider/UserContext";
import { useRoles } from "hooks/useRoles";
import { PERMISSIONS } from "constants/portal";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import "cxfcomponents/LayoutContainers/DashboardLayout/portalPage.css";
import "./styles.css";
import { fetchFlowById } from "api/FlowDesignerApis";

const FlowSelectionDialog = () => {
	const { t } = useTranslation("flowDesigner");
	const { user } = useAuth0();
	const dispatch = useDispatch();
	const { showFlowListDialog, flows, userFlows } = useSelector(
		(store) => store?.designer?.flows
	);
	const { users } = useSelector((store) => store?.designer?.users);
	const [sharedFlows, setSharedFlows] = useState([]);
	const { userData } = useContext(UserContext);
	const { roles } = useRoles(false);
	const { userPermissions } = useContext(PermissionsContext);
	const tabMap = useRef(new Map());
	const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);

	const handleFilterFlow = (flow) => {
		const userRole = roles?.find((role) => role.id === userData?.role_id);
		const viewAllWorkflowPermission = userPermissions.find(
			(permission) => permission.name === PERMISSIONS.VIEW_ANY_WORKFLOWS
		);

		const isFlowInSharedFlows = sharedFlows.find(
			(sharedFlow) => sharedFlow.id === flow.id
		);

		if (isFlowInSharedFlows) return false;

		if (
			userRole?.name?.toLowerCase() === "owner" ||
			viewAllWorkflowPermission?.value
		) {
			return true;
		}

		return flow.created_by_id === user?.cxfUserId;
	};

	const getStatus = (flow) => {
		if (flow.trashed) return "deleted";

		if (flow.shared_status === "test") return "test";

		if (flow.steps === "") return "draft";

		return "active";
	};

	const dataTableData = useMemo(() => {
		return {
			columns: [
				{
					Header: "flowId",
					accessor: "flowId",
					show: false
				},
				{
					Header: "icon",
					accessor: "icon",
					canGroupBy: false,
					disableSortBy: true,
					width: 84
				},
				{
					Header: "name",
					accessor: "nameWithBadge",
					width: 250,
					canGroupBy: false,
					Cell: ({ value }) => (
						<Tooltip title={value?.name || ""} arrow>
							<SoftBox
								className="white-to-dark"
								fontSize="14px"
								fontWeight="bold"
								sx={{
									maxWidth: "210px",
									whiteSpace: "normal",
									wordBreak: "break-word",
									display: "-webkit-box",
									WebkitBoxOrient: "vertical",
									WebkitLineClamp: 2,
									overflow: "hidden"
								}}
							>
								{value?.name}
							</SoftBox>
						</Tooltip>
					),
					sortType: (rowA, rowB, columnId) => {
						const nameA = rowA.values[columnId].name;
						const nameB = rowB.values[columnId].name;
						return nameA.localeCompare(nameB);
					}
				},
				{
					Header: "name",
					accessor: "name",
					canGroupBy: false,
					show: false
				},
				{
					Header: "description",
					accessor: "description",
					canGroupBy: false,
					Cell: ({ value }) => {
						return (
							<SoftBox>
								<TextWithEllipsis
									value={{
										name: value,
										maxWidth: "clamp(100px, 20vw, 300px)"
									}}
								/>
							</SoftBox>
						);
					}
				},
				{
					Header: "status",
					accessor: "status",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <StatusBadge status={value} />;
					}
				},
				{
					Header: "shared status",
					accessor: "sharedStatus",
					canGroupBy: false,
					grouped: false,
					expanded: true
				},
				{
					Header: "users",
					accessor: "users",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <UsersCell users={value} />;
					},
					disableSortBy: true
				},
				{
					Header: "last edited",
					accessor: "lastEdited",
					canGroupBy: false,
					Cell: ({ value }) => <SoftBox sx={{ maxWidth: "165px", whiteSpace: "normal", lineHeight: 1.4 }}>{value}</SoftBox>
				}
			],
			rows:
				flows && sharedFlows
					? [
							...flows.filter(handleFilterFlow).map((item) => {
								const OwnerFromFlow = item.created_by_id;

								const OwnerUser =
									users?.filter((owner) =>
										OwnerFromFlow.includes(owner.auth0_id)
									) || [];

								const getUsersFromUF = userFlows?.filter(
									(uf) => uf.flow_id === item.id
								);

								const sharedToUsers = getUsersFromUF?.map((userid) => {
									return userid.user_id;
								});

								const sharedUsers =
									users?.filter((sharedTo) =>
										sharedToUsers?.includes(sharedTo.auth0_id)
									) || [];

								const mergedArrayforOwner = [
									...OwnerUser,
									...sharedUsers
								];

								return {
									flowId: item.id,
									icon: (
										<SoftBox
											component="img"
											borderRadius="12px"
											border="0.5px solid #e6e6e6"
											sx={{
												objectFit: "cover",
												width: "60px",
												height: "60px"
											}}
											src={item?.logo || defaultFlowIcon}
										/>
									),
									nameWithBadge: {
										trashed: item.trashed,
										name: item.name,
										id: item.id
									},
									name: item.name,
									description: item.description,
									// publishStatus:
									//   item.publish_status === "ForApproval" ? "For Approval" : item.publish_status,
									sharedStatus:
										item?.created_by_id === user?.cxfUserId
											? "Owner"
											: "",
									status: `${getStatus(item)}`,
									users: mergedArrayforOwner,
									lastEdited:
										getLocalDateTimeString(
											item.updated_date_time ||
												item.created_date_time
										) || ""
								};
							}),
							...sharedFlows
								.filter((item) => !item.trashed)
								.map((item) => {
									const fromFlows = flows?.filter(
										(f) => f.id === item.id
									);

									const OwnerFromFlow = fromFlows?.map((userid) => {
										return userid.created_by_id;
									});

									const OwnerUser =
										users?.filter((owner) =>
											OwnerFromFlow?.includes(owner.auth0_id)
										) || [];

									const getUsersFromUF = userFlows?.filter(
										(uf) => uf.flow_id === item.id
									);

									const sharedToUsers = getUsersFromUF?.map(
										(userid) => {
											return userid.user_id;
										}
									);

									const sharedUsers =
										users?.filter((sharedTo) =>
											sharedToUsers?.includes(sharedTo.auth0_id)
										) || [];

									const mergedArray = [...OwnerUser, ...sharedUsers];

									return {
										flowId: item.id,
										icon: (
											<SoftBox
												component="img"
												borderRadius="12px"
												border="0.5px solid #e6e6e6"
												sx={{
													objectFit: "cover",
													width: "60px",
													height: "60px"
												}}
												src={item?.logo || defaultFlowIcon}
											/>
										),
										nameWithBadge: {
											trashed: item.trashed,
											name: item.name,
											id: item.id
										},
										name: item.name,
										description: item.description,
										sharedStatus: "Shared",
										status: `${getStatus(item)}`,
										users: mergedArray,
										lastEdited:
											getLocalDateTimeString(
												item.updated_date_time ||
													item.created_date_time
											) || ""
									};
								})
						]
					: []
		};
	}, [flows, userFlows, sharedFlows, roles]);

	const closeDialog = () => {
		dispatch(closeFlowListDialog());
	};

	const handleRowClick = async (row) => {
		if (!row?.flowId) return;

		setTimeout(() => dispatch(closeFlowListDialog()), 100);

		let tab = tabMap.current.get(row.flowId);

		if (!tab || tab.closed) {
			if (!currentSavedFlow) {
				const flow = await fetchFlowById(user?.stripeId, row.flowId);

				if (!flow) return;

				dispatch(setSavedFlow(flow));

				return;
			}

			tab = window.open(
				`/designer/${row.flowId}`,
				"_blank",
				"noopener,noreferrer"
			);
			tabMap.current.set(row.flowId, tab);

			return;
		}

		tab.focus();
	};

	useEffect(() => {
		if (users && userFlows) {
			const filteredFlows = [];
			const sharedUserFlows = userFlows.filter(
				(item) => item.user_id === user?.cxfUserId
			);

			sharedUserFlows.forEach((sharedFlow) => {
				const flowId = sharedFlow.flow_id;
				const flow = flows.find((flow) => flow.id === flowId);

				if (flow) {
					filteredFlows.push({
						...flow,
						userAccessType: sharedFlow.access_type
					});
				}
			});
			setSharedFlows(filteredFlows);
		}
	}, [userFlows, flows]);

	useEffect(() => {
		if (showFlowListDialog) {
			dispatch(fetchUsers(user?.stripeId));
			dispatch(fetchUserFlows(user?.stripeId));
		}
	}, [showFlowListDialog]);

	return (
		<DialogWrapper
			open={showFlowListDialog}
			closeDialog={closeDialog}
		>
			{flows.length ? (
				<DataTable
					hover
					table={dataTableData}
					onRowClick={handleRowClick}
					entriesPerPage={{ defaultValue: 10 }}
					hiddenTopButtons
					withSearchbar
					tableView="list"
					tableHeight="100%"
				/>
			) : (
				<NoDataIcon message={t("messages.no_saved_flows")} />
			)}
		</DialogWrapper>
	);
};

export default FlowSelectionDialog;
